import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loadUser } = useAuth();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const error = searchParams.get('error');

      if (error) {
        // Handle error cases
        const errorMessages = {
          missing_code: 'Authorization code is missing',
          no_email: 'Email not provided by Google',
          account_disabled: 'Your account has been disabled',
          auth_failed: 'Authentication failed. Please try again'
        };

        const errorMessage = errorMessages[error] || 'Login failed. Please try again';
        setStatus('error');

        // Wait a moment before redirecting to show error
        setTimeout(() => {
          navigate('/login', { state: { error: errorMessage } });
        }, 1500);
        return;
      }

      if (token && refreshToken) {
        try {
          // Save tokens to localStorage
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);

          setStatus('success');

          // Load user data and wait for it to complete
          await loadUser();

          // Give React time to update state before navigating
          // This ensures Header component will have the updated user state
          setTimeout(() => {
            navigate('/dashboard');
          }, 500);
        } catch (err) {
          console.error('Failed to load user:', err);
          setStatus('error');
          setTimeout(() => {
            navigate('/login', { state: { error: 'Failed to load user data. Please try logging in again.' } });
          }, 1500);
        }
      } else {
        // Missing tokens
        setStatus('error');
        setTimeout(() => {
          navigate('/login', { state: { error: 'Authentication tokens missing' } });
        }, 1500);
      }
    };

    handleCallback();
  }, [searchParams, navigate, loadUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Completing sign in...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="rounded-full h-12 w-12 bg-green-100 mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <p className="mt-4 text-gray-600">Sign in successful! Redirecting...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="rounded-full h-12 w-12 bg-red-100 mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <p className="mt-4 text-gray-600">Sign in failed. Redirecting...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
